/* eslint-disable react/require-default-props */
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
   previewRefiltekImport,
   confirmRefiltekImport,
} from '../../../../services/routing/routingApi';
import RestoreItemDialog from '../../RoutingItemShow/components/RestoreItemDialog';

import BatchIntro from './batch/BatchIntro';
import UploadModal from './batch/UploadModal';
import BatchReview from './batch/BatchReview';
import ConfirmImportModal from './batch/ConfirmImportModal';
import RouteCatalog from './batch/RouteCatalog';
import { BatchShell } from './batch/styles';
import {
   SCHEDULE_PERIOD_MORNING,
   SCHEDULE_PERIOD_AFTERNOON,
   buildListingRedirectUrl,
   rowIsDuplicate,
} from './batch/importHelpers';

// ===========================================================================
// BatchImportTab — orquestrador da importação em lote (rework 0060). Modelo de
// estados (não mais wizard de passos):
//   sem previewData → <BatchIntro> (estado vazio)
//   com previewData → <BatchReview> (revisão em cards)
//   <UploadModal>   → upload como modal (prefilled), disparado pelo intro (ou,
//                     no Slice 2, pelo + de uma célula do catálogo)
//
// A seleção mora aqui (o confirm precisa dela). O service (preview/confirm) é
// reusado sem alteração — os flags novos (has_gps/existing_item) já vêm crus.
// ===========================================================================
export default function BatchImportTab({
   scheduleDate,
   schedulePeriod,
   onScheduleDateChange,
   onSchedulePeriodChange,
   clusterOrigin = 'GOIANIA',
   clusterOrigins = [],
   onClusterOriginChange,
   canRestore = false,
   canViewCatalog = false,
}) {
   const navigate = useNavigate();

   // Alvo do UploadModal ({ scheduleDate, schedulePeriod, clusterOrigin }) — não
   // nulo = modal aberto. Semeia o formulário do modal.
   const [uploadTarget, setUploadTarget] = useState(null);
   const [isAnalyzing, setIsAnalyzing] = useState(false);

   // Resultado analisado + o alvo que o gerou (usado no header da revisão e no
   // confirm — NÃO o estado compartilhado, que o operador pode ter mudado).
   const [previewData, setPreviewData] = useState(null);
   const [analyzedTarget, setAnalyzedTarget] = useState(null);
   const [selectedFile, setSelectedFile] = useState(null);
   const [selectedLineNumbers, setSelectedLineNumbers] = useState(() => new Set());

   const [isConfirmOpen, setIsConfirmOpen] = useState(false);
   const [isConfirming, setIsConfirming] = useState(false);

   // Duplicata CANCELADA restaurável a partir de um card ({ id, order_number }).
   const [restoreTarget, setRestoreTarget] = useState(null);

   // ---------------------- Upload modal ----------------------
   const openUpload = useCallback(
      (seed) => {
         setUploadTarget(
            seed ?? {
               scheduleDate,
               schedulePeriod,
               clusterOrigin,
            },
         );
      },
      [scheduleDate, schedulePeriod, clusterOrigin],
   );

   const handleReupload = useCallback(() => {
      openUpload(analyzedTarget ?? undefined);
   }, [openUpload, analyzedTarget]);

   const handleAnalyze = useCallback(
      async (form) => {
         setIsAnalyzing(true);
         try {
            const result = await previewRefiltekImport({
               file:           form.file,
               scheduleDate:   form.scheduleDate,
               schedulePeriod: form.schedulePeriod,
               clusterOrigin:  form.clusterOrigin,
            });

            // Marcação default: válidas + avisos importáveis. Duplicatas
            // (existing_item != null) começam DESMARCADAS — o confirm delas
            // daria 409. Sem-GPS seguem marcadas (importáveis, só não
            // roteirizáveis até geocodar).
            const initial = new Set();
            (result.validRows || []).forEach((r) => initial.add(r.lineNumber));
            (result.warningRows || []).forEach((r) => {
               if (!rowIsDuplicate(r)) initial.add(r.lineNumber);
            });

            setPreviewData(result);
            setSelectedLineNumbers(initial);
            setSelectedFile(form.file);
            setAnalyzedTarget({
               scheduleDate:   form.scheduleDate,
               schedulePeriod: form.schedulePeriod,
               clusterOrigin:  form.clusterOrigin,
               fileName:       form.file?.name ?? null,
            });

            // Sincroniza o estado compartilhado (persiste entre abas e semeia o
            // próximo upload com o último alvo usado).
            onScheduleDateChange?.(form.scheduleDate);
            onSchedulePeriodChange?.(form.schedulePeriod);
            onClusterOriginChange?.(form.clusterOrigin);

            setUploadTarget(null);
         } catch (err) {
            // Erros do preview (400 file_required / 415 / 422): toast com a
            // mensagem do backend; o modal fica aberto pro operador corrigir.
            const apiMessage =
               err?.response?.data?.errors?.[0]?.message ??
               err?.response?.data?.message ??
               'Erro ao analisar o arquivo.';
            toast.error(apiMessage);
         } finally {
            setIsAnalyzing(false);
         }
      },
      [onScheduleDateChange, onSchedulePeriodChange, onClusterOriginChange],
   );

   // ---------------------- Seleção ----------------------
   const handleToggleLine = useCallback((lineNumber) => {
      setSelectedLineNumbers((prev) => {
         const next = new Set(prev);
         if (next.has(lineNumber)) next.delete(lineNumber);
         else next.add(lineNumber);
         return next;
      });
   }, []);

   const handleSetSelection = useCallback((lineNumbers, selected) => {
      setSelectedLineNumbers((prev) => {
         const next = new Set(prev);
         lineNumbers.forEach((n) => {
            if (selected) next.add(n);
            else next.delete(n);
         });
         return next;
      });
   }, []);

   // ---------------------- Confirm ----------------------
   const handleOpenConfirm = useCallback(() => {
      if (selectedLineNumbers.size === 0) {
         toast.error('Selecione ao menos um item para importar.');
         return;
      }
      setIsConfirmOpen(true);
   }, [selectedLineNumbers]);

   const handleConfirm = useCallback(async () => {
      if (isConfirming || !analyzedTarget) return;
      setIsConfirming(true);
      try {
         const result = await confirmRefiltekImport({
            file:                selectedFile,
            scheduleDate:        analyzedTarget.scheduleDate,
            schedulePeriod:      analyzedTarget.schedulePeriod,
            clusterOrigin:       analyzedTarget.clusterOrigin,
            selectedLineNumbers: Array.from(selectedLineNumbers),
         });

         const batch = result.importBatch ?? {};
         const imported = batch.total_imported ?? 0;
         const skipped  = batch.total_skipped  ?? 0;
         const errors   = batch.total_errors   ?? 0;
         const fallback = [`${imported} item(s) importado(s)`];
         if (skipped > 0) fallback.push(`${skipped} pulado(s)`);
         if (errors > 0)  fallback.push(`${errors} com erro`);

         toast.success(result.message ?? fallback.join(' · '));
         setIsConfirmOpen(false);
         navigate(
            buildListingRedirectUrl(
               analyzedTarget.scheduleDate,
               analyzedTarget.schedulePeriod,
            ),
         );
      } catch (err) {
         // 409 duplicate_import (o lote inteiro é rejeitado) e demais erros:
         // toast com a mensagem do backend, preservando a revisão e a seleção.
         // O atalho de restore por-card já é o caminho pra duplicata cancelada.
         const apiMessage =
            err?.response?.data?.message ?? 'Erro ao confirmar a importação.';
         toast.error(apiMessage);
         setIsConfirmOpen(false);
      } finally {
         setIsConfirming(false);
      }
   }, [
      isConfirming,
      analyzedTarget,
      selectedFile,
      selectedLineNumbers,
      navigate,
   ]);

   // ---------------------- Restore de duplicata cancelada ----------------------
   // Após o restore, o pedido vira duplicata ATIVA (não some da chave) → some o
   // botão e a nota deixa de dizer "(cancelado)". Atualiza localmente sem
   // re-analisar, preservando a seleção do operador.
   const applyRestored = useCallback((restoredId) => {
      setPreviewData((prev) => {
         if (!prev) return prev;
         const patch = (rows) =>
            (rows || []).map((r) => {
               const ex = r.existing_item;
               if (ex && ex.id === restoredId && ex.item_status === 'CANCELADO') {
                  return { ...r, existing_item: { ...ex, item_status: 'PENDENTE' } };
               }
               return r;
            });
         return {
            ...prev,
            validRows: patch(prev.validRows),
            warningRows: patch(prev.warningRows),
         };
      });
   }, []);

   // ---------------------- Render ----------------------
   return (
      <BatchShell>
         {/* Catálogo só no estado inicial — durante a revisão (previewData) ele
             sai de cena em vez de ficar num limbo "desabilitado". */}
         {canViewCatalog && !previewData && (
            <RouteCatalog
               clusterOrigin={clusterOrigin}
               clusterOrigins={clusterOrigins}
               onImport={openUpload}
            />
         )}

         {previewData ? (
            <BatchReview
               previewData={previewData}
               target={analyzedTarget}
               clusterOrigins={clusterOrigins}
               selectedLineNumbers={selectedLineNumbers}
               canRestore={canRestore}
               isConfirming={isConfirming}
               onToggleLine={handleToggleLine}
               onSetSelection={handleSetSelection}
               onReupload={handleReupload}
               onConfirm={handleOpenConfirm}
               onRequestRestore={setRestoreTarget}
            />
         ) : (
            <BatchIntro onImport={() => openUpload()} />
         )}

         <UploadModal
            isOpen={Boolean(uploadTarget)}
            target={uploadTarget}
            clusterOrigins={clusterOrigins}
            isAnalyzing={isAnalyzing}
            onClose={() => setUploadTarget(null)}
            onAnalyze={handleAnalyze}
         />

         <ConfirmImportModal
            isOpen={isConfirmOpen}
            onClose={() => (isConfirming ? null : setIsConfirmOpen(false))}
            onConfirm={handleConfirm}
            isSubmitting={isConfirming}
            selectedCount={selectedLineNumbers.size}
            scheduleDate={analyzedTarget?.scheduleDate}
            schedulePeriod={analyzedTarget?.schedulePeriod}
         />

         <RestoreItemDialog
            isOpen={Boolean(restoreTarget)}
            onClose={() => setRestoreTarget(null)}
            item={restoreTarget}
            onRestored={(restored) => {
               const id = restored?.id ?? restoreTarget?.id;
               if (id) applyRestored(id);
               setRestoreTarget(null);
            }}
         />
      </BatchShell>
   );
}

BatchImportTab.propTypes = {
   scheduleDate:           PropTypes.string,
   schedulePeriod:         PropTypes.oneOf([
      SCHEDULE_PERIOD_MORNING,
      SCHEDULE_PERIOD_AFTERNOON,
   ]),
   onScheduleDateChange:   PropTypes.func,
   onSchedulePeriodChange: PropTypes.func,
   clusterOrigin:          PropTypes.string,
   clusterOrigins:         PropTypes.arrayOf(
      PropTypes.shape({
         origin_code:  PropTypes.string,
         origin_label: PropTypes.string,
      }),
   ),
   onClusterOriginChange:  PropTypes.func,
   canRestore:             PropTypes.bool,
   canViewCatalog:         PropTypes.bool,
};
